import type {
  AuthOptions,
  AuthResponse,
  ChangePasswordInput,
  HealthResponse,
  LoginInput,
  MeResponse,
  RegisterUserInput,
  UpdateAccountInput,
  User,
  UserSession,
} from '@orbit-sync/shared-types';
import { request } from './client.js';

/** `POST /api/v1/auth/register`. Unauthenticated by definition. */
export function register(input: RegisterUserInput): Promise<AuthResponse> {
  return request<AuthResponse>('/auth/register', {
    method: 'POST',
    body: input,
    authenticated: false,
  });
}

/** `POST /api/v1/auth/login`. */
export function login(input: LoginInput): Promise<AuthResponse> {
  return request<AuthResponse>('/auth/login', {
    method: 'POST',
    body: input,
    authenticated: false,
  });
}

/** `GET /api/v1/auth/me`. Used to validate a stored token on startup. */
export function me(signal?: AbortSignal): Promise<MeResponse> {
  return request<MeResponse>('/auth/me', { signal });
}

/** `GET /api/v1/health`. No token required. */
export function health(signal?: AbortSignal): Promise<HealthResponse> {
  return request<HealthResponse>('/health', { authenticated: false, signal });
}

/** `GET /api/v1/auth/options`: whether directory sign-in is on. Public. */
export function options(signal?: AbortSignal): Promise<AuthOptions> {
  return request<AuthOptions>('/auth/options', { authenticated: false, signal });
}

/**
 * `POST /api/v1/auth/logout`: ends the session on the server. `keepalive`, so
 * it still goes out when sign-out navigates away at once.
 */
export function logout(): Promise<void> {
  return request<void>('/auth/logout', { method: 'POST', keepalive: true });
}

/** `GET /api/v1/auth/sessions`: this person's signed-in browsers and devices. */
export function listSessions(signal?: AbortSignal): Promise<UserSession[]> {
  return request<UserSession[]>('/auth/sessions', { signal });
}

export function endSession(sessionId: string): Promise<void> {
  return request<void>(`/auth/sessions/${sessionId}`, { method: 'DELETE' });
}

/** "Sign out everywhere else". */
export function endOtherSessions(): Promise<{ revoked: number }> {
  return request<{ revoked: number }>('/auth/sessions', { method: 'DELETE' });
}

/** `PATCH /api/v1/auth/me`. Answers with a new token carrying the new name/email. */
export function updateAccount(input: UpdateAccountInput): Promise<AuthResponse> {
  return request<AuthResponse>('/auth/me', { method: 'PATCH', body: input });
}

export function changePassword(input: ChangePasswordInput): Promise<AuthResponse> {
  return request<AuthResponse>('/auth/password', { method: 'POST', body: input });
}

export function setAvatar(dataUrl: string): Promise<User> {
  return request<User>('/auth/me/avatar', { method: 'PUT', body: { dataUrl } });
}

export function removeAvatar(): Promise<User> {
  return request<User>('/auth/me/avatar', { method: 'DELETE' });
}
